import * as THREE from 'three';
import { dangerOf, GEAR, ITEMS, TOOLS, heldFor, isImprovised, isToolId, NPCS, creatureDef, isCreatureKind, isGearId, type ActorInfo, type ActorMove, type Anim, type GameMap, type HeldTool, type ItemId, type Look, type Mood } from '@sunderfen/shared';
import { G, addMesh, buildCharacter, freeTree, mat, setHeldTool, type CharacterModel } from './models.js';
import { itemModel } from './icon3d.js';
import { armourMeshes, buildCreature, weaponMesh, type CreatureModel } from './creature-models.js';
import { CLIPS, NEUTRAL, applyPose, blend, fullPose, gripOf, heavyStroke, holdPose, sample, strokeFor, type Clip, type Grip, type Pose } from './anim.js';

const LINE_MAT = new THREE.LineBasicMaterial({ color: 0xe8e4da, transparent: true, opacity: 0.75 });
const BOBBER_GEO = G.oct(0.06);
const tmp = new THREE.Vector3(), tmp2 = new THREE.Vector3();
const METAL = [0x8a7a68, 0xc48a3f, 0xa3aab1, 0xbff0ff, 0x7a7d86, 0xd8c070];

/** A bow held in the left hand: grip at the fist, limbs up and down, string toward the archer. */
function bowInHand(model: string, metal: number) {
  const g = new THREE.Group(), R = model === 'longbow' ? 0.62 : 0.46, wood = metal === 5 ? 0x8a6a3e : METAL[metal] ?? 0x8a6a3e;
  const arc = new THREE.Mesh(new THREE.TorusGeometry(R, 0.026, 4, 14, Math.PI * 0.8), mat(wood));
  arc.rotation.z = -Math.PI * 0.9;
  const frame = new THREE.Group(); frame.rotation.y = Math.PI / 2; frame.position.y = R; frame.add(arc); g.add(frame);
  const string = addMesh(g, G.box(0.006, 0.006, 2 * R * Math.sin(Math.PI * 0.4)), 0xe8e4da, 0, R - R * Math.cos(Math.PI * 0.4), 0);
  string.name = 'string';
  addMesh(g, G.box(0.06, 0.06, 0.1), 0x5a3a20, 0, 0, 0);
  return g;
}
function arrowNocked() {
  const g = new THREE.Group();
  addMesh(g, G.box(0.02, 0.58, 0.02), 0xc8a070, 0, -0.05, 0);
  addMesh(g, G.box(0.04, 0.07, 0.04), 0xa3aab1, 0, -0.36, 0);
  addMesh(g, G.box(0.06, 0.09, 0.008), 0xf0f0e8, 0, 0.2, 0);
  return g;
}
function tongs() {
  const g = new THREE.Group();
  addMesh(g, G.box(0.025, 0.025, 0.5), 0x3a3a3e, -0.02, 0, 0.22).rotation.y = 0.05;
  addMesh(g, G.box(0.025, 0.025, 0.5), 0x3a3a3e, 0.02, 0, 0.22).rotation.y = -0.05;
  addMesh(g, G.box(0.08, 0.03, 0.12), 0xd8703a, 0, 0, 0.5);
  return g;
}

/** A fading ribbon behind a swinging weapon or tool. Additive, so dark means invisible. */
class Trail {
  readonly mesh: THREE.Mesh;
  private pts: { a: THREE.Vector3; b: THREE.Vector3; age: number }[] = [];
  private geo = new THREE.BufferGeometry();
  private pos: Float32Array; private col: Float32Array;
  constructor(private n = 12, color = 0xfff2d8) {
    this.pos = new Float32Array(n * 2 * 3); this.col = new Float32Array(n * 2 * 3);
    this.geo.setAttribute('position', new THREE.BufferAttribute(this.pos, 3));
    this.geo.setAttribute('color', new THREE.BufferAttribute(this.col, 3));
    const idx: number[] = []; for (let i = 0; i < n - 1; i++) { const a = i * 2; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
    this.geo.setIndex(idx);
    this.mesh = new THREE.Mesh(this.geo, new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, toneMapped: false }));
    this.mesh.frustumCulled = false; this.mesh.visible = false; this.mesh.renderOrder = 5;
    this.base = new THREE.Color(color);
  }
  private base: THREE.Color;
  push(a: THREE.Vector3, b: THREE.Vector3) { this.pts.unshift({ a: a.clone(), b: b.clone(), age: 0 }); if (this.pts.length > this.n) this.pts.pop(); }
  update(dt: number) {
    for (const p of this.pts) p.age += dt;
    this.pts = this.pts.filter(p => p.age < 0.16);
    this.mesh.visible = this.pts.length > 1;
    if (!this.mesh.visible) return;
    for (let i = 0; i < this.n; i++) {
      const p = this.pts[Math.min(i, this.pts.length - 1)], k = Math.max(0, 1 - p.age / 0.16) * (1 - i / this.n) * 0.85;
      this.pos.set([p.a.x, p.a.y, p.a.z, p.b.x, p.b.y, p.b.z], i * 6);
      this.col.set([this.base.r * k * 0.2, this.base.g * k * 0.2, this.base.b * k * 0.2, this.base.r * k, this.base.g * k, this.base.b * k], i * 6);
    }
    (this.geo.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    (this.geo.getAttribute('color') as THREE.BufferAttribute).needsUpdate = true;
  }
}

/**
 * A player, townsperson or creature as the client sees it. The server sends the tiles an actor
 * stepped through each tick; we glide along them over one tick so movement looks smooth while
 * staying exactly on the server's path. Actions arrive as pulses: each one plays a stroke from
 * anim.ts, and the stroke's impact frames call `onImpact` so effects land on the beat.
 */
/** The invisible volume a tap or click on an actor hits (a unit cylinder, scaled per actor). */
const HIT_GEO = new THREE.CylinderGeometry(1, 1, 1, 8);
/** Strokes of a fight, which carry on while you walk. */
const FIGHT_CLIPS = new Set(['slash', 'punch', 'cleave', 'slam', 'draw']);
const HIT_MAT = new THREE.MeshBasicMaterial({ visible: false });

export class ActorView {
  readonly model: CharacterModel;
  readonly tag: HTMLDivElement;
  private bubble: HTMLDivElement | null = null;
  private bubbleUntil = 0;
  /** Rendered position (tile coordinates, centre of tile = +0.5). */
  pos = new THREE.Vector2();
  private from = new THREE.Vector2();
  private settle = new THREE.Vector2();
  private pts: THREE.Vector2[] = [];
  private start = 0;
  private glideMs = 600;
  /** When the last glide ended: the walk cycle carries on briefly so tick boundaries don't show. */
  private stopAt = -1e9;
  private face = 0;
  private faceTo = 0;
  private walkT = 0;
  anim: Anim = 'idle';
  tool: HeldTool = null;
  moving = false;
  tileX: number;
  tileZ: number;
  readonly npc?: string;
  /** Fishing line and float, shown once a rod has been cast. */
  private line: THREE.Line;
  private bobber: THREE.Mesh;
  private lineOut = false;
  private biteUntil = 0;
  /** A fish held up after a catch. */
  private held: THREE.Mesh | null = null;
  private heldUntil = 0;
  /** Creature kind and model, for animals and monsters. */
  readonly mob?: string;
  readonly cm?: CreatureModel;
  hp = 0; maxHp = 0;
  private hpBar: HTMLDivElement | null = null;
  private hpShownUntil = 0;
  private lungeT = 0;
  private knockT = 0;
  deadAt = 0;
  /** A creature that is a person (a looter, a raider): drawn and animated like a player. */
  private person = false;
  private look: Look | null = null;
  private armour: THREE.Object3D[] = [];
  private flashUntil = 0;
  private flashing = false;
  private ownMats = false;
  /** Home for the night: the person is hidden and a lantern hangs at their door to knock on. */
  indoors = false;
  private lantern: THREE.Object3D | null = null;
  private atHome: HTMLElement | null = null;
  setIndoors(on: boolean) {
    if (on === this.indoors) return;
    this.indoors = on;
    for (const c of this.model.root.children) if (!c.userData.hitProxy && c !== this.lantern) c.visible = !on;
    if (on && !this.lantern) {
      const g = new THREE.Group(), rs = this.model.root.scale;
      const glow = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.22, 0.16), new THREE.MeshBasicMaterial({ color: 0xffc070, toneMapped: false }));
      glow.position.y = 1.55; g.add(glow);
      const cap = new THREE.Mesh(new THREE.ConeGeometry(0.15, 0.1, 4), new THREE.MeshLambertMaterial({ color: 0x2a2420 })); cap.position.y = 1.71; cap.rotation.y = Math.PI / 4; g.add(cap);
      const post = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.04, 1.5, 6), new THREE.MeshLambertMaterial({ color: 0x3a2e24 })); post.position.y = 0.75; g.add(post);
      g.scale.set(1 / rs.x, 1 / rs.y, 1 / rs.z);
      g.traverse(o => { o.userData.actorId = this.id; });
      this.lantern = g; this.model.root.add(g);
    }
    if (this.lantern) this.lantern.visible = on;
    if (on && !this.atHome) { this.atHome = document.createElement('small'); this.atHome.className = 'athome'; this.atHome.textContent = 'at home · knock'; }
    if (this.atHome) { if (on) this.tag.appendChild(this.atHome); else this.atHome.remove(); }
  }
  private qmark: HTMLSpanElement | null = null;
  /** A gold ! over someone with work for you, a ? over someone your quest needs now. */
  setQuestMark(m: 'offer' | 'step' | null) {
    if (!m) { this.qmark?.remove(); this.qmark = null; return; }
    if (!this.qmark) { this.qmark = document.createElement('span'); this.tag.prepend(this.qmark); }
    this.qmark.className = `qmark qmark-${m}`; this.qmark.textContent = m === 'offer' ? '!' : '?';
    this.qmark.title = m === 'offer' ? 'Has work for you' : 'Your quest needs them';
  }
  /* animation */
  private pose: Pose = fullPose({});
  private target: Pose = fullPose({});
  private hold: Pose = fullPose({});
  private clip: Clip | null = null;
  private clipT = 0;
  private grip: Grip = 'fists';
  private weaponModel: string | undefined;
  private arrow: THREE.Object3D | null = null;
  private trail: Trail | null = null;
  private lvlTag: HTMLElement | null = null;
  private lvl = 0;
  /** Combat level shown on the tag (players and creatures). */
  combatLvl = 0;
  /** Colour a creature's level by how it compares with ours: green easy .. red deadly. */
  setDanger(mine: number) { if (this.lvlTag) this.lvlTag.className = `lv-${dangerOf(mine, this.lvl)}`; }
  private tip = new THREE.Object3D();
  private tipBase = new THREE.Object3D();
  /** Something an action stroke hit landed: chips, splash, sparks, the hit splat. */
  onImpact: ((a: ActorView, beat: string) => void) | null = null;

  constructor(readonly id: number, readonly name: string, info: ActorInfo, readonly isSelf: boolean, private layer: THREE.Object3D, tagLayer: HTMLElement, private tickMs: number) {
    this.npc = info.npc;
    this.combatLvl = info.lvl ?? 0;
    this.mob = info.mob;
    const pd = info.mob && isCreatureKind(info.mob) ? creatureDef(info.mob).person : undefined;
    if (pd) {
      // people: drawn and animated like players, holding their weapon
      this.model = buildCharacter(hashName(`${name}${id}`), pd.outfit);
      this.hp = info.hp ?? 1; this.maxHp = info.maxHp ?? 1;
      this.person = true;
    } else if (info.mob && isCreatureKind(info.mob)) {
      this.cm = buildCreature(creatureDef(info.mob));
      // creatures don't have human limbs; give the shared code harmless stand-ins
      const dummy = () => new THREE.Group();
      this.model = { root: this.cm.root, upper: dummy(), body: dummy(), head: dummy(), face: dummy(), legL: dummy(), legR: dummy(), armL: dummy(), armR: dummy(), tool: dummy(), toolL: dummy() };
      this.hp = info.hp ?? 1; this.maxHp = info.maxHp ?? 1;
    } else this.model = buildCharacter(hashName(name), info.npc ? NPCS[info.npc]?.outfit : undefined);
    // an invisible, generous hit volume: small creatures (rabbits, chickens) are easy to tap
    {
      const h = this.cm ? Math.max(0.7, this.cm.tagY) : 1.9, rs = this.model.root.scale;
      const proxy = new THREE.Mesh(HIT_GEO, HIT_MAT);
      proxy.scale.set(0.6 / rs.x, h / rs.y, 0.6 / rs.z); proxy.position.y = h / 2 / rs.y;
      proxy.userData.hitProxy = true;
      this.model.root.add(proxy);
    }
    this.model.root.traverse((o: THREE.Object3D) => { o.userData.actorId = id; });
    if (isSelf) this.model.root.traverse((o: THREE.Object3D) => { o.userData.self = true; });
    this.model.root.userData.mob = !!info.mob; this.model.root.userData.view = this;
    layer.add(this.model.root);
    this.tileX = info.x; this.tileZ = info.z;
    this.pos.set(info.x + 0.5, info.z + 0.5); this.settle.copy(this.pos);
    this.face = this.faceTo = info.face;
    this.tag = document.createElement('div');
    this.tag.className = 'nametag' + (isSelf ? ' self' : '') + (info.npc ? ' npc' : '') + (info.mob ? ' mob' : '') + (info.mob && creatureDef(info.mob as never)?.boss ? ' boss' : '');
    this.tag.textContent = name;
    if (info.title) { const t = document.createElement('small'); t.textContent = info.title; this.tag.appendChild(t); }
    else if (info.lvl && !isSelf) { const t = document.createElement('small'); t.textContent = info.mob && creatureDef(info.mob as never)?.boss ? 'boss' : `level ${info.lvl}`; this.tag.appendChild(t); if (info.mob) { this.lvlTag = t; this.lvl = info.lvl; } }
    if (info.mob) this.ensureBar();
    if (info.indoors) this.setIndoors(true);
    if (info.mood) this.setMood(info.mood);
    if (info.look) this.setLook(info.look);
    else if (pd?.weapon) this.setLook({ w: pd.weapon, a: [0, 0, 0] });
    tagLayer.appendChild(this.tag);
    const lg = new THREE.BufferGeometry(); lg.setAttribute('position', new THREE.BufferAttribute(new Float32Array(9), 3));
    this.line = new THREE.Line(lg, LINE_MAT); this.line.frustumCulled = false; this.line.visible = false;
    const bob = new THREE.Mesh(BOBBER_GEO, new THREE.MeshBasicMaterial({ color: 0xe0402a }));
    addTop(bob);
    this.bobber = bob; bob.visible = false;
    layer.add(this.line, this.bobber);
    this.setVisual(info.anim, info.tool, 0);
    // already fishing when we arrived: the line is out
    if (info.anim === 'fish' && info.tool === 'rod') this.lineOut = true;
    if (info.anim === 'die') { this.clip = CLIPS.fall; this.clipT = CLIPS.fall.dur; }
    Object.assign(this.pose, this.hold);
  }

  /** A fish is on the line: the float dips until `until` (ms). */
  bite(until: number) { this.biteUntil = until; }
  /** Hold up what was just caught for a moment. */
  showCatch(item: ItemId, now: number) {
    if (this.held) { this.model.armL.remove(this.held); freeTree(this.held); this.held = null; }
    const icon = ITEMS[item]?.icon as { color?: string; size?: number; shape: string } | undefined;
    const col = new THREE.Color(icon?.color ?? '#b8c4c8').getHex();
    const size = (icon?.size ?? 1) * (item.startsWith('legend') ? 1.8 : 1);
    const geo = icon?.shape === 'crab' || icon?.shape === 'lobster' ? G.box(0.22, 0.08, 0.16) : icon?.shape === 'eel' ? G.box(0.05, 0.05, 0.5) : G.oct(0.12);
    const m = new THREE.Mesh(geo, new THREE.MeshLambertMaterial({ color: col }));
    if (icon?.shape === 'fish') m.scale.set(0.55 * size, 0.9 * size, 2.0 * size);
    m.position.set(0, -0.5, 0.12); m.rotation.x = Math.PI / 2;
    this.model.armL.add(m);
    this.held = m; this.heldUntil = now + 1500;
    if (this.tool === 'rod') this.play(CLIPS.reel);
  }

  /** What's in each hand for this activity. */
  private dress(anim: Anim, tool: HeldTool, tier: number) {
    // a swing in a fight comes as "gear:<id>": that's the weapon in hand, not a tool
    const swung = typeof tool === 'string' && tool.startsWith('gear:') ? tool.slice(5) : null;
    if (swung) tool = null;
    const wid = swung ?? this.look?.w;
    const m = this.model, weapon = wid && isGearId(wid) && !isImprovised(wid) ? GEAR[wid] : null;
    // something that isn't a weapon, held to fight with: a hatchet, a log, a fish
    const makeshift = !weapon && wid && isImprovised(wid) ? wid : null;
    if (makeshift && !tool && (anim === 'fight' || anim === 'idle' || anim === 'walk')) {
      const key = `${anim}|makeshift|${makeshift}`;
      if (key === m.root.userData.dressed) return;
      m.root.userData.dressed = key;
      const clear = (g: THREE.Object3D) => { while (g.children.length) { const c = g.children[0]; g.remove(c); freeTree(c); } };
      const held = heldFor(makeshift), t = isToolId(makeshift) ? TOOLS[makeshift] : null;
      // swing it like the weapon it most resembles: hatchets, picks, hammers and clubs chop down hard, the rest slash
      clear(m.tool); clear(m.toolL); this.arrow = null;
      this.weaponModel = held === 'axe' || held === 'pick' || held === 'hammer' || held === 'club' ? 'battleaxe' : 'sword';
      this.grip = gripOf(this.weaponModel);
      if (held === 'item' || held === 'club') {
        // the item itself, as it looks in the pack, gripped like a club
        const it = itemModel(makeshift);
        if (it) { it.scale.setScalar(0.32); it.rotation.set(Math.PI / 2, 0, 0); it.position.set(0, 0, 0.22); m.tool.add(it); }
        else setHeldTool(m, held, 0, ITEMS[makeshift as ItemId]?.icon as { color?: string } | undefined);
      } else setHeldTool(m, held, Math.max(0, t?.metal ?? 0));
      this.tip.position.set(0, 0, 0.6); this.tipBase.position.set(0, 0, 0.25); m.tool.add(this.tip, this.tipBase);
      m.tool.traverse((o: THREE.Object3D) => { o.userData.actorId = this.id; });
      return;
    }
    const fighting = !tool && weapon && (anim === 'fight' || anim === 'shoot' || anim === 'idle' || anim === 'walk');
    const key = `${anim}|${tool}|${fighting ? weapon!.model : ''}|${tier}`;
    if (key === m.root.userData.dressed) return;
    m.root.userData.dressed = key;
    const clear = (g: THREE.Object3D) => { while (g.children.length) { const c = g.children[0]; g.remove(c); freeTree(c); } };
    clear(m.tool); clear(m.toolL); this.arrow = null;
    this.weaponModel = fighting ? weapon!.model : undefined;
    this.grip = gripOf(this.weaponModel);
    if (fighting) {
      if (this.grip === 'bow') { m.toolL.add(bowInHand(weapon!.model ?? 'shortbow', weapon!.metal)); this.arrow = arrowNocked(); this.arrow.rotation.x = Math.PI / 2; this.arrow.position.set(0, 0.02, 0.05); m.tool.add(this.arrow); }
      else m.tool.add(weaponMesh(weapon!.model ?? 'sword', weapon!.metal));
    } else if (tool) setHeldTool(m, tool, tier);
    else if (anim === 'cook') setHeldTool(m, 'skewer');
    else if (anim === 'craft') setHeldTool(m, 'knife');
    else if (anim === 'eat') setHeldTool(m, 'food');
    if (anim === 'smith' && tool === 'hammer') m.toolL.add(tongs());
    // swing trail anchors near the head of whatever is held
    this.tip.position.set(0, 0, this.grip === 'two' ? 0.7 : 0.6); this.tipBase.position.set(0, 0, 0.25);
    m.tool.add(this.tip, this.tipBase);
    m.tool.traverse((o: THREE.Object3D) => { o.userData.actorId = this.id; });
    m.toolL.traverse((o: THREE.Object3D) => { o.userData.actorId = this.id; });
  }

  /** New animation state from the server. `pulse` plays one stroke of it. */
  setVisual(anim: Anim, tool: HeldTool, tier: number, pulse = false) {
    const fresh = anim !== this.anim;
    if (this.cm) {
      if ((anim === 'fight' || anim === 'shoot') && (pulse || fresh)) this.lungeT = 0.4;
      this.anim = anim; this.tool = tool;
      return;
    }
    this.dress(anim, tool, tier);
    if (fresh && !(anim === 'fish' && tool === 'rod')) this.lineOut = false;
    this.anim = anim; this.tool = tool;
    this.hold = fullPose(holdPose(anim, tool, this.grip));
    if (anim === 'die') { if (fresh) this.play(CLIPS.fall); return; }
    if (fresh && this.clip?.name === 'fall') this.clip = null;
    if (pulse || (fresh && anim !== 'idle' && anim !== 'walk')) {
      const s = (anim === 'fight' && heavyStroke(this.weaponModel)) || strokeFor(anim, tool, this.grip, fresh);
      if (s) this.play(s);
    }
  }
  private play(c: Clip) { this.clip = c; this.clipT = 0; }

  /** Took a hit (or dodged one): a short reaction over whatever else is going on. */
  react(miss: boolean, now: number) {
    if (this.cm) { if (!miss) this.knockT = 0.25; return; }
    if (this.anim === 'die') return;
    if (!this.clip || this.clip.name === 'flinch' || this.clip.name === 'dodge' || this.clipT > (this.clip.impacts?.[0]?.[0] ?? 0) + 0.1) this.play(miss ? CLIPS.dodge : CLIPS.flinch);
    if (!miss) this.flash(now);
  }
  /** Milliseconds until the current attack stroke lands (so the splat appears on the blow). */
  impactDelay() {
    if (this.cm) return this.lungeT > 0 ? Math.max(0, (this.lungeT - 0.2) * 1000) : 150;
    const c = this.clip, hit = c?.impacts?.find(i => i[1] === 'hit' || i[1] === 'slam' || i[1] === 'release');
    return c && hit ? Math.max(0, (hit[0] - this.clipT) * 1000) : 150;
  }

  /** Worn weapon and armour, as other players see them. */
  setLook(look: Look) {
    this.look = look;
    for (const o of this.armour) { o.parent?.remove(o); freeTree(o); }
    this.armour = [];
    const a = armourMeshes(look.a);
    if (look.a[0]) { this.model.face.add(a.head); this.armour.push(a.head); }
    if (look.a[1]) { this.model.body.add(a.body); this.armour.push(a.body); }
    if (look.a[2]) { this.model.legL.add(a.legs[0]); this.model.legR.add(a.legs[1]); this.armour.push(...a.legs); }
    // a helm replaces the hat
    if (this.model.hat) this.model.hat.visible = !look.a[0];
    for (const o of this.armour) o.traverse((x: THREE.Object3D) => { x.userData.actorId = this.id; });
    this.model.root.userData.dressed = '';
    this.dress(this.anim, this.tool, 0);
    this.hold = fullPose(holdPose(this.anim, this.tool, this.grip));
    this.ownMats = false;
  }

  /** A raider's state of mind, over its head: "?" looking, "!" coming for you, running, given up. */
  private moodEl: HTMLSpanElement | null = null;
  mood: Mood = 'calm';
  setMood(m: Mood) {
    this.mood = m;
    if (m === 'calm') { this.moodEl?.remove(); this.moodEl = null; return; }
    if (!this.moodEl) { this.moodEl = document.createElement('span'); this.tag.prepend(this.moodEl); }
    this.moodEl.className = `mood mood-${m}`;
    this.moodEl.textContent = m === 'wary' ? '?' : m === 'alert' ? '!' : m === 'flee' ? 'running' : 'gave up';
    this.moodEl.classList.remove('pop'); void this.moodEl.offsetWidth; this.moodEl.classList.add('pop');
  }

  private ensureBar() {
    if (this.hpBar) return;
    this.hpBar = document.createElement('div'); this.hpBar.className = 'hpbar'; this.hpBar.innerHTML = '<i></i>';
    this.tag.appendChild(this.hpBar);
  }
  /** Hitpoints changed (a hit landed). Shows the bar for a while. */
  setHp(hp: number, max: number, now: number) {
    this.hp = hp; this.maxHp = max;
    this.ensureBar();
    (this.hpBar!.firstChild as HTMLElement).style.width = `${Math.max(0, Math.min(1, hp / Math.max(1, max))) * 100}%`;
    this.hpShownUntil = now + 8000;
    this.flash(now);
  }
  private flash(now: number) { this.flashUntil = now + 130; }
  /** Fallen. Creatures fade away; people lie still until they wake at the spawn. */
  die(now: number) {
    if (this.cm) { this.deadAt = now; this.tag.hidden = true; }
    else if (this.person) { this.setVisual('die', null, 0); this.tag.hidden = true; }
  }

  applyMove(m: ActorMove, now: number, tier: number) {
    if (m.indoors !== undefined) this.setIndoors(m.indoors);
    if (m.jump && this.anim === 'die') { this.clip = null; Object.assign(this.pose, NEUTRAL); }
    // a stroke starting where we stand ends the between-ticks stride at once
    if (m.pulse && !m.path.length) { this.stopAt = -1e9; this.pos.copy(this.settle); }
    this.setVisual(m.anim, m.tool, tier, !!m.pulse);
    if (m.caught) this.showCatch(m.caught, now);
    this.faceTo = m.face;
    if (!m.path.length) return;
    const last = m.path[m.path.length - 1];
    this.tileX = last[0]; this.tileZ = last[1];
    if (m.jump) { this.pts = []; this.pos.set(last[0] + 0.5, last[1] + 0.5); this.settle.copy(this.pos); this.stopAt = -1e9; return; }
    // carry on from where we're drawn (no snap to the end of the last glide), through this tick's tiles.
    // Each glide runs a touch longer than a tick, so a packet arriving a little late still finds us moving.
    this.from.copy(this.pos);
    this.pts = m.path.map(([x, z]) => new THREE.Vector2(x + 0.5, z + 0.5));
    this.start = now;
    const dist = this.pts.reduce((d, p, i) => d + p.distanceTo(i ? this.pts[i - 1] : this.from), 0);
    this.glideMs = this.tickMs * (dist > 0.01 ? 1.12 : 1);
  }

  say(text: string, now: number, tagLayer: HTMLElement) {
    if (!this.bubble) { this.bubble = document.createElement('div'); this.bubble.className = 'bubble'; tagLayer.appendChild(this.bubble); }
    this.bubble.textContent = text;
    this.bubbleUntil = now + 5000;
  }

  /** The point a stroke's tool reaches, in world space (for effects). */
  facing() { return this.face; }

  update(dt: number, now: number, map: GameMap, cleared: ReadonlySet<number>, camera: THREE.Camera, rect: DOMRect) {
    this.moving = false;
    if (this.pts.length) {
      // move at an even speed along the whole path (by distance, not by tile), and round the corners
      const t = Math.min(1, (now - this.start) / this.glideMs);
      const segs = [this.from, ...this.pts], lens = segs.slice(1).map((p, i) => p.distanceTo(segs[i])), total = lens.reduce((a, b) => a + b, 0) || 1;
      let d = t * total, i = 0;
      while (i < lens.length - 1 && d > lens[i]) { d -= lens[i]; i++; }
      const a = segs[i], b = segs[i + 1], k = lens[i] ? d / lens[i] : 1;
      const px = a.x + (b.x - a.x) * k, pz = a.y + (b.y - a.y) * k;
      // ease toward the path rather than sitting exactly on it: corners come out as curves
      const ease = Math.min(1, dt * 14);
      this.pos.set(this.pos.x + (px - this.pos.x) * ease, this.pos.y + (pz - this.pos.y) * ease);
      if (b.x !== a.x || b.y !== a.y) this.faceTo = Math.atan2(b.x - a.x, b.y - a.y);
      this.moving = true;
      if (t >= 1) { this.pts = []; this.stopAt = now; this.settle.copy(segs[segs.length - 1]); }
    } else if (now - this.stopAt < 160) {
      // between ticks: keep the stride going and drift onto the tile centre
      this.moving = true;
      this.pos.lerp(this.settle, Math.min(1, dt * 14));
    } else if (this.stopAt > 0) this.pos.lerp(this.settle, Math.min(1, dt * 14));
    let df = this.faceTo - this.face;
    while (df > Math.PI) df -= Math.PI * 2;
    while (df < -Math.PI) df += Math.PI * 2;
    this.face += df * Math.min(1, dt * 12);
    if (this.cm) { this.updateCreature(dt, now, map, cleared); this.updateFlash(now); return; }
    void camera; void rect;
    this.animate(dt, now);
    const m = this.model, p = this.pose, sc = m.root.scale.y;
    const y = map.surfaceY(this.pos.x, this.pos.y, cleared);
    const bob = this.moving ? Math.abs(Math.sin(this.walkT)) * 0.05 : 0;
    m.root.position.set(this.pos.x + Math.sin(this.face) * p.fwd * sc, y + bob - p.drop * sc, this.pos.y + Math.cos(this.face) * p.fwd * sc);
    m.root.rotation.set(p.fall, this.face, 0, 'YXZ');
    this.updateFlash(now);
    this.updateLine(now);
  }

  /** Walk cycle, strokes, reactions and a little idle life, blended into one pose. */
  private animate(dt: number, now: number) {
    const m = this.model, tg = this.target;
    if (this.held && (now > this.heldUntil || this.moving)) { m.armL.remove(this.held); freeTree(this.held); this.held = null; }
    // strokes advance; impact frames fire their beats
    let rate = 12;
    // walking cancels a stroke, except a blow in a fight: chasing something, you still swing as you close in
    const fightStroke = !!this.clip && FIGHT_CLIPS.has(this.clip.name);
    if (this.clip && (this.moving && this.anim !== 'die') && !fightStroke) this.clip = null;
    if (this.clip) {
      const c = this.clip, t0 = this.clipT;
      this.clipT += dt;
      for (const [at, beat] of c.impacts ?? []) if (t0 < at && this.clipT >= at) this.beat(beat);
      if (c.trail && this.clipT >= c.trail[0] && this.clipT <= c.trail[1]) this.sampleTrail();
      sample(c, Math.min(this.clipT, c.dur), this.hold, tg);
      rate = 26;
      if (this.clipT >= c.dur && !c.stay) this.clip = null;
    } else Object.assign(tg, this.hold);
    if (this.moving) {
      this.walkT += dt * 9;
      const sw = Math.sin(this.walkT) * 0.6;
      // in a fight, the arms keep the weapon up (or keep swinging it); only the legs walk
      const armed = this.anim === 'fight' || this.anim === 'shoot' || (!!this.clip && FIGHT_CLIPS.has(this.clip.name));
      if (!armed) Object.assign(tg, NEUTRAL);
      tg.legL = sw; tg.legR = -sw;
      if (!armed) { tg.armLx = -sw * 0.8; tg.armRx = sw * 0.8; tg.lean = 0.06; tg.twist = Math.sin(this.walkT) * 0.08; }
      if (this.grip === 'bow' && (this.anim === 'shoot' || this.anim === 'idle')) tg.armLx = -0.4;
      rate = 16;
    } else {
      this.walkT = 0;
      if (this.anim === 'fight' && !this.clip) {
        // squared up between blows: weight shifting, the weapon bobbing, ready
        tg.lean += Math.sin(now / 400 + this.id) * 0.03;
        tg.armRx += Math.sin(now / 330 + this.id) * 0.07;
        tg.legL += 0.05; tg.legR -= 0.05;
      }
      if (this.anim === 'idle' && !this.clip) {
        // breathing, shifting weight, glancing about
        tg.lean += Math.sin(now / 900 + this.id) * 0.012;
        tg.tilt += Math.sin(now / 2600 + this.id) * 0.03;
        tg.headY += Math.sin(now / 3100 + this.id) * 0.35 * Math.max(0, Math.sin(now / 7300 + this.id * 1.7));
        tg.armLz -= 0.04; tg.armRz += 0.04;
      }
    }
    if (this.held) tg.armLx = -2.5 + Math.sin(now / 120) * 0.08;
    // sneaking: low and leaning, arms in
    if (this.look?.s && this.anim !== 'die') { tg.lean = Math.max(tg.lean, 0.62); tg.drop = Math.max(tg.drop, 0.12); tg.head -= 0.45; tg.armLx -= 0.45; tg.armRx -= 0.45; if (!this.moving) { tg.legL = -0.75; tg.legR = 0.35; } else { tg.legL -= 0.35; tg.legR -= 0.35; } }
    blend(this.pose, tg, dt, rate);
    applyPose(m, this.pose);
    if (this.arrow) this.arrow.visible = !(this.clip?.name === 'draw' && this.clipT > 0.5) && (this.anim === 'shoot');
    this.trail?.update(dt);
  }

  private beat(beat: string) {
    if (beat === 'cast') this.lineOut = true;
    this.onImpact?.(this, beat);
  }

  private sampleTrail() {
    if (!this.trail) { this.trail = new Trail(); this.layer.add(this.trail.mesh); }
    this.model.root.updateMatrixWorld(true);
    this.tip.getWorldPosition(tmp); this.tipBase.getWorldPosition(tmp2);
    this.trail.push(tmp2, tmp);
  }

  private updateFlash(now: number) {
    const on = now < this.flashUntil;
    if (on === this.flashing) return;
    this.flashing = on;
    const root = this.model.root;
    if (!this.ownMats) {
      // materials are shared between everyone with the same colours; take our own copies first
      root.traverse((o: THREE.Object3D) => { const mm = (o as THREE.Mesh).material as THREE.Material | undefined; if (mm && (mm as THREE.MeshLambertMaterial).emissive && !mm.userData.own) { const c = mm.clone(); c.userData.own = true; (o as THREE.Mesh).material = c; } });
      this.ownMats = true;
    }
    root.traverse((o: THREE.Object3D) => { const mm = (o as THREE.Mesh).material as THREE.MeshLambertMaterial | undefined; if (mm?.emissive && mm.userData.own) mm.emissive.setHex(on ? 0x6a2a22 : 0x000000); });
  }

  private updateCreature(dt: number, now: number, map: GameMap, cleared: ReadonlySet<number>) {
    const c = this.cm!, d = creatureDef(this.mob as never);
    this.walkT = this.moving ? this.walkT + dt * 10 : this.walkT * 0.9;
    const sw = Math.sin(this.walkT) * 0.7;
    c.legs.forEach((l, i) => { l.rotation.x = (i % 2 ? sw : -sw) * (i >= 2 ? -1 : 1); });
    const flap = d.model === 'bat' ? Math.sin(now / 60) : d.model === 'chicken' && this.moving ? Math.sin(now / 50) * 0.6 : Math.sin(now / 400) * 0.1;
    c.wings.forEach((w, i) => { if (d.model === 'bat' || d.model === 'chicken') w.rotation.z = (i ? -1 : 1) * flap * 0.8; else w.rotation.x = -0.2 + Math.sin(now / 500 + i) * 0.1; });
    // an attack, shaped to the animal: pecks, headbutts, pounces, swoops
    this.lungeT = Math.max(0, this.lungeT - dt);
    this.knockT = Math.max(0, this.knockT - dt);
    const k = this.lungeT > 0 ? 1 - this.lungeT / 0.4 : 0, arc = Math.sin(k * Math.PI);
    let lunge = 0, hop = 0, pitch = 0;
    switch (d.model) {
      case 'chicken': case 'frog': lunge = arc * 0.12; pitch = Math.max(0, Math.sin(k * Math.PI * 2)) * 0.6; break;
      case 'cow': case 'sheep': case 'boar': lunge = arc * 0.32; pitch = arc * 0.35; break;
      case 'wolf': case 'rabbit': lunge = arc * 0.45; hop = arc * 0.22; pitch = (k < 0.5 ? -1 : 1) * arc * 0.25; break;
      case 'bat': case 'wraith': lunge = arc * 0.4; hop = -arc * 0.3; pitch = arc * 0.3; break;
      default: lunge = arc * 0.35; pitch = (k < 0.4 ? -arc : arc) * 0.3;
    }
    const knock = Math.sin((this.knockT / 0.25) * Math.PI) * 0.16;
    c.body.position.z = lunge - knock;
    c.body.rotation.x = pitch;
    c.body.scale.y = 1 - knock * 0.4;
    if (this.lungeT > 0) c.wings.forEach(w => { w.rotation.x = -1.2 * arc; });
    let y = map.surfaceY(this.pos.x, this.pos.y, cleared) + hop;
    if (c.floats) y = Math.max(y, 0) + 0.15 + Math.sin(now / 600 + this.id) * 0.12;
    else if (this.moving) y += Math.abs(Math.sin(this.walkT)) * 0.04;
    if (this.deadAt) {
      const kk = Math.min(1, (now - this.deadAt) / 900);
      c.root.rotation.z = kk * 1.4; y -= kk * 0.5;
      c.root.traverse((o: THREE.Object3D) => { const mm = (o as THREE.Mesh).material as THREE.Material | undefined; if (mm && !mm.userData.cloned) { (o as THREE.Mesh).material = mm.clone(); ((o as THREE.Mesh).material as THREE.Material).userData.cloned = true; } const m2 = (o as THREE.Mesh).material as THREE.Material & { opacity: number } | undefined; if (m2) { m2.transparent = true; m2.opacity = 1 - kk; } });
    }
    c.root.position.set(this.pos.x, y, this.pos.y);
    c.root.rotation.y = this.face;
  }

  private updateLine(now: number) {
    const fishing = this.anim === 'fish' && this.tool === 'rod' && !this.moving && this.lineOut;
    const tip = fishing ? this.model.tool.getObjectByName('tip') : undefined;
    this.line.visible = this.bobber.visible = !!tip;
    if (!tip) return;
    this.model.root.updateMatrixWorld(true);
    tip.getWorldPosition(tmp);
    const bx = this.pos.x + Math.sin(this.face) * 1.75, bz = this.pos.y + Math.cos(this.face) * 1.75;
    const biting = now < this.biteUntil;
    const by = biting ? -0.06 + Math.sin(now / 40) * 0.03 : 0.03 + Math.sin(now / 420 + this.id) * 0.02;
    this.bobber.position.set(bx, by, bz);
    const a = this.line.geometry.getAttribute('position') as THREE.BufferAttribute;
    a.setXYZ(0, tmp.x, tmp.y, tmp.z);
    a.setXYZ(1, (tmp.x + bx) / 2, (tmp.y + by) / 2 - (biting ? 0.05 : 0.25), (tmp.z + bz) / 2);
    a.setXYZ(2, bx, by + 0.04, bz);
    a.needsUpdate = true;
  }

  /** Position the name tag and chat bubble. Call after the camera has moved this frame. */
  placeTags(now: number, camera: THREE.Camera, rect: DOMRect) {
    const y = this.model.root.position.y + (this.cm ? 0 : this.pose.drop * this.model.root.scale.y);
    const v = new THREE.Vector3(this.pos.x, y + (this.cm ? this.cm.tagY : 1.95 * this.model.root.scale.y), this.pos.y).project(camera);
    const visible = !this.deadAt && v.z < 1 && v.x > -1.2 && v.x < 1.2 && v.y > -1.2 && v.y < 1.2;
    if (this.hpBar) this.hpBar.hidden = !(now < this.hpShownUntil || (this.maxHp && this.hp < this.maxHp && !this.cm));
    const sx = ((v.x + 1) / 2) * rect.width, sy = ((1 - v.y) / 2) * rect.height;
    this.tag.hidden = !visible;
    if (visible) this.tag.style.transform = `translate(${sx}px, ${sy}px) translate(-50%, -100%)`;
    if (this.bubble) {
      if (now > this.bubbleUntil) { this.bubble.remove(); this.bubble = null; }
      else { this.bubble.hidden = !visible; this.bubble.style.transform = `translate(${sx}px, ${sy - 22}px) translate(-50%, -100%)`; }
    }
  }

  dispose(layer: THREE.Object3D) { layer.remove(this.model.root, this.line, this.bobber); freeTree(this.model.root); freeTree(this.line); freeTree(this.bobber); if (this.trail) { layer.remove(this.trail.mesh); freeTree(this.trail.mesh); } this.tag.remove(); this.bubble?.remove(); }
}

/** White top half on a red float. */
function addTop(bob: THREE.Mesh) {
  const top = new THREE.Mesh(BOBBER_GEO, new THREE.MeshBasicMaterial({ color: 0xf4f0e6 }));
  top.scale.set(0.8, 0.6, 0.8); top.position.y = 0.035; bob.add(top);
}

function hashName(s: string) { let h = 0; for (const c of s.toLowerCase()) h = (h * 31 + c.charCodeAt(0)) | 0; return h; }
